import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Clock, Phone, Star, Utensils, Wine, Coffee } from 'lucide-react';
import { heroImages, galleryImages, services, contactInfo, openingHours, kitchenHours } from '../mock';

function useSlideshow(images, interval = 6000) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % images.length), interval);
    return () => clearInterval(t);
  }, [images.length, interval]);
  return i;
}

export default function Home() {
  const idx = useSlideshow(heroImages);

  return (
    <div className="bg-[#faf6f0]">
      {/* HERO */}
      <section className="relative h-screen min-h-[640px] w-full overflow-hidden">
        {heroImages.map((src, i) => (
          <div key={src} className={`absolute inset-0 transition-opacity duration-[1500ms] ${i === idx ? 'opacity-100' : 'opacity-0'}`}>
            <img src={src} alt="KSV" className="w-full h-full object-cover scale-105" style={{ transform: i === idx ? 'scale(1.08)' : 'scale(1.02)', transition: 'transform 8s ease-out' }} />
          </div>
        ))}
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80" />
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-6">
          <span className="text-amber-300 tracking-[0.4em] text-xs md:text-sm uppercase mb-6 font-medium">Herzlich willkommen in Wien</span>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl text-white leading-none max-w-5xl">
            KSV<span className="text-amber-400">.</span> Sportkantine
          </h1>
          <p className="mt-6 text-amber-50/90 max-w-2xl text-lg md:text-xl font-light">
            Authentische österreichische Küche im Herzen von Wien — herzhaft, ehrlich, mit Liebe zubereitet.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-4">
            <Link to="/kontakt" className="px-8 py-4 rounded-full bg-amber-500 hover:bg-amber-400 text-[#1a1410] font-semibold tracking-wide shadow-2xl hover:shadow-amber-500/40 hover:-translate-y-0.5 transition-all inline-flex items-center gap-2">
              Tisch reservieren <ArrowRight size={18}/>
            </Link>
            <Link to="/wochenmenue" className="px-8 py-4 rounded-full border-2 border-white/80 text-white hover:bg-white hover:text-[#1a1410] font-semibold tracking-wide transition-all">
              Wochenmenü ansehen
            </Link>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2">
            {heroImages.map((_, i) => (
              <span key={i} className={`h-1 rounded-full transition-all ${i === idx ? 'w-10 bg-amber-400' : 'w-4 bg-white/40'}`} />
            ))}
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <div>
            <span className="text-amber-600 tracking-[0.3em] text-xs uppercase">Willkommen</span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1a1410] mt-4 leading-tight">
              KSV-Sportkantine Restaurant
            </h2>
            <div className="w-16 h-[3px] bg-amber-500 mt-6 mb-6"/>
            <p className="text-[#3d332b] leading-relaxed text-lg">
              Willkommen in der KSV-Sportkantine im schönen Wien. Wir freuen uns, Sie als Gast begrüßen zu dürfen! In unserem Bistro erwartet Sie ein freundlicher Empfang und authentische österreichische Küche. Unsere schmackhaften Burger und Schnitzel erwarten Sie!
            </p>
            <p className="text-[#5a4a3d] leading-relaxed mt-4">
              Bringen Sie Ihre Freunde und Familie mit. Wir bieten auch vegetarische Optionen, erfrischendes Bier, Wein, Kaffee und Tee. Genießen Sie in ruhiger, entspannter Umgebung ein schmackhaftes Frühstück und Mittagessen.
            </p>
            <div className="grid grid-cols-3 gap-6 mt-10">
              {[{icon: Utensils, label: 'Österreichische Küche'}, {icon: Wine, label: 'Bier &amp; Wein'}, {icon: Coffee, label: 'Kaffee &amp; Kuchen'}].map((f, i) => (
                <div key={i} className="text-center">
                  <div className="mx-auto w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 mb-2"><f.icon size={22}/></div>
                  <div className="text-xs uppercase tracking-widest text-[#5a4a3d]" dangerouslySetInnerHTML={{__html: f.label}}/>
                </div>
              ))}
            </div>
          </div>
          <div className="relative">
            <div className="aspect-[4/5] rounded-lg overflow-hidden shadow-2xl">
              <img src={galleryImages[3].url} alt="Speise" className="w-full h-full object-cover"/>
            </div>
            <div className="absolute -bottom-8 -left-8 w-40 h-40 rounded-lg overflow-hidden shadow-2xl border-8 border-[#faf6f0] hidden md:block">
              <img src={galleryImages[6].url} alt="Dessert" className="w-full h-full object-cover"/>
            </div>
            <div className="absolute -top-6 -right-6 bg-amber-500 text-[#1a1410] px-6 py-4 rounded-lg shadow-xl hidden md:flex items-center gap-2">
              <Star size={18} fill="currentColor"/>
              <div>
                <div className="font-serif text-lg leading-none">Wiener Tradition</div>
                <div className="text-xs uppercase tracking-widest opacity-70">seit vielen Jahren</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ESSENZ */}
      <section className="py-20 px-6 bg-[#1a1410] text-amber-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src={galleryImages[10].url} alt="" className="w-full h-full object-cover"/>
        </div>
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <span className="text-amber-400 tracking-[0.3em] text-xs uppercase">Unsere Philosophie</span>
          <h2 className="font-serif text-4xl md:text-5xl mt-4">Essenz kulinarischer Exzellenz</h2>
          <div className="w-16 h-[3px] bg-amber-500 mx-auto mt-6 mb-8"/>
          <p className="text-lg leading-relaxed text-amber-50/85">
            Unser Angebot zum Mitnehmen ermöglicht es Ihnen, unsere Mahlzeiten bequem von zu Hause aus zu genießen. Bei uns finden Sie Außenservice, Klimaanlage, Parkplätze und kostenloses WLAN. Sie möchten eine Veranstaltung ausrichten? Wir bieten Ihnen erstklassige Dienstleistungen.
          </p>
          <p className="mt-4 text-amber-50/70">
            Jeder ist bei uns willkommen — auch Ihre Kinder in unserer Kinderecke und Ihre Haustiere.
          </p>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-amber-600 tracking-[0.3em] text-xs uppercase">Was wir bieten</span>
            <h2 className="font-serif text-4xl md:text-5xl text-[#1a1410] mt-4">Unsere Services</h2>
            <div className="w-16 h-[3px] bg-amber-500 mx-auto mt-6"/>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {services.slice(0, 10).map((s) => (
              <div key={s.id} className="group bg-white rounded-xl p-6 text-center shadow-sm hover:shadow-2xl hover:-translate-y-1 transition-all border border-amber-100/60">
                <div className="mx-auto w-14 h-14 rounded-full bg-amber-50 group-hover:bg-amber-500 group-hover:text-white text-amber-700 flex items-center justify-center mb-3 transition-colors">
                  <ServiceIcon name={s.icon} size={22}/>
                </div>
                <div className="font-serif text-[#1a1410] text-sm">{s.title}</div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/services" className="inline-flex items-center gap-2 text-amber-700 hover:text-amber-900 font-semibold tracking-wide">
              Alle Services entdecken <ArrowRight size={16}/>
            </Link>
          </div>
        </div>
      </section>

      {/* INFO STRIP */}
      <section className="py-16 px-6 bg-[#f3ebdd]">
        <div className="max-w-6xl mx-auto grid md:grid-cols-3 gap-8">
          <InfoCard icon={MapPin} title="Adresse" lines={[contactInfo.address, `${contactInfo.city}, ${contactInfo.country}`]}/>
          <InfoCard icon={Clock} title="Öffnungszeiten" lines={['Mo – Sa: 09:00 – 23:00', 'So: 09:00 – 13:00 (nur Bar)', `Küche: ${kitchenHours}`]} more/>
          <InfoCard icon={Phone} title="Reservierung" lines={[contactInfo.phone, contactInfo.email]}/>
        </div>
      </section>
    </div>
  );
}

function InfoCard({ icon: Icon, title, lines, more }) {
  return (
    <div className="bg-white rounded-xl p-8 shadow-sm border border-amber-100">
      <div className="w-12 h-12 rounded-full bg-amber-500 text-white flex items-center justify-center mb-4"><Icon size={22}/></div>
      <h3 className="font-serif text-2xl text-[#1a1410] mb-3">{title}</h3>
      <ul className="space-y-1 text-[#5a4a3d]">
        {lines.map((l, i) => <li key={i}>{l}</li>)}
        {more && <li className="text-amber-700 text-sm mt-2"><Link to="/kontakt">Alle Zeiten anzeigen →</Link></li>}
      </ul>
    </div>
  );
}

// service icon mapper
import * as LucideIcons from 'lucide-react';
function ServiceIcon({ name, size }) {
  const Icon = LucideIcons[name] || LucideIcons.Circle;
  return <Icon size={size}/>;
}
